/**
 * Converts a hex color to RGB values
 */
export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  // Remove # if present
  hex = hex.replace("#", "");

  // Handle both 3-digit and 6-digit hex
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    console.warn("Invalid hex color:", hex);
    return null;
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

/**
 * Calculates relative luminance for RGB values
 * Using the formula from WCAG 2.0
 */
export const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Determines if text should be white or black based on background color
 * Returns true if the background is dark (should use white text)
 * Returns false if the background is light (should use black text)
 *
 * @param backgroundColor - Hex color code (with or without #)
 * @returns boolean - true for white text, false for black text
 */
export const shouldUseWhiteText = (backgroundColor: string): boolean => {
  const rgb = hexToRgb(backgroundColor);

  if (!rgb) {
    console.warn("Invalid background color, defaulting to black text");
    return false;
  }

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // Using WCAG 2.0 recommended contrast ratio threshold
  // Adjusted threshold for better contrast with light colors
  // If luminance is low (dark background), use white text
  // If luminance is high (light background), use black text
  return luminance < 0.45; // Lowered threshold to ensure better contrast with light colors
};

/**
 * Gets the appropriate text color (black or white) based on background color
 *
 * @param backgroundColor - Hex color code (with or without #)
 * @returns string - '#FFFFFF' for white or '#000000' for black
 */
export const getContrastText = (backgroundColor: string): string => {
  return shouldUseWhiteText(backgroundColor) ? "#FFFFFF" : "#000000";
};
